<template>
  <div>

    <v-card class="rounded-lg shadow">
      <v-overlay :value="overlay" absolute color="white" opacity="0.5">
        <v-progress-circular
            color="primary"
            indeterminate
            size="50"
        ></v-progress-circular>
      </v-overlay>

      <v-card-title>

        <div>
          <v-text-field
              v-model="keyword"
              append-icon="mdi-magnify"
              @keydown.enter="getData"
              hint="Appuyez sur ↵ Entrée pour rechercher"
              clearable
              @click:clear="[keyword=null,getData()]"
              dense
              filled
              persistent-hint
              placeholder="Ref-XXXXXX"
              rounded
          ></v-text-field>
        </div>

        <v-spacer/>

        <v-btn color="grey"
               text
               @click="getData">
          <v-icon left>mdi-refresh</v-icon>
          Mettre à jour
        </v-btn>

        <v-btn color="grey"
               text
        >
          <v-icon left>mdi-filter-outline</v-icon>
          Filtrer
        </v-btn>

        <v-btn color="gifty"
               @click="$refs.addParcelDialogRef.open()"
               class="white--text"
               depressed>
          <v-icon left>mdi-package-variant</v-icon>
          Ajouter un Coli
        </v-btn>

      </v-card-title>

      <v-divider/>

      <v-card-text v-if="isLoading">
        <v-skeleton-loader type="table"/>
      </v-card-text>

      <div v-else>
        <v-card-text>
          <v-simple-table class="table-border rounded-lg" v-if="parcels.length">
            <template v-slot:default>
              <thead>
              <tr>
                <th>Méthode</th>
                <th>Référence</th>
                <th class="text-no-wrap">Utilisateur (Nom)</th>
                <th class="text-no-wrap">Utilisateur (Tél)</th>
                <th>Statut</th>
                <th>Créé par</th>
                <th>Créé le</th>
                <th class="text-no-wrap">Montant total</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item ,index) in parcels" :key="index">

                <td>
                  {{ item.delivery_method }}
                </td>

                <td class="font-weight-bold gifty-text">
                  {{ item.reference }}
                </td>

                <td class="font-weight-medium">
                  {{ item.user?.name }}
                </td>

                <td class="font-weight-medium">
                  {{ item.user?.phone }}
                </td>

                <td>
                  <v-btn :loading="loadingStatus[item.id]"
                         depressed
                         small
                         dark
                         :color="statusColor(item.status)"
                         class="text-uppercase">
                    {{ item.status }}
                  </v-btn>
                </td>

                <td>
                  {{ item.created_by.name }}
                </td>

                <td class="text-no-wrap">
                  {{ item.created_at }}
                </td>

                <td class="font-weight-bold gifty-text">
                  {{ CurrencyFormatting(item.total_amount) }}
                </td>

                <td>
                  <div class="d-flex align-center">
                    <v-btn icon
                           color="gifty"
                           disabled
                           @click="remove(item.id)"
                    >
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>

                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn color="gifty"
                               :disabled="!!(item.status !== 'shipped')"
                               @click="printOrderSlip(item.id)"
                               icon
                               v-bind="attrs"
                               v-on="on"
                        >
                          <v-icon>mdi-file-pdf-box</v-icon>
                        </v-btn>
                      </template>
                      <span>Imprimer le Bordereau</span>
                    </v-tooltip>

                    <v-btn text color="gifty" class="ml-2 v-btn--active"
                           @click="$refs.addCardsDialogRef.open(item)">
                      <v-icon left>mdi-credit-card-multiple-outline</v-icon>
                      {{ item.cards_count }}
                      <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>
                </td>

              </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div v-else>
            <NoResults @reload="getData"/>
          </div>

        </v-card-text>
        <v-divider/>

        <v-card-actions>

          <div :style="{width: '120px'}">
            <v-select v-model="perPage"
                      :items="[10,20,30,50,100]"
                      dense
                      hide-details
                      label="Ligne par page"
                      outlined></v-select>
          </div>

          <v-spacer/>


          <small class="text--darken-2 grey--text">
            {{ pageStart }}-{{ pageEnd }} de {{ total }}
          </small>
          <v-pagination v-model="pagination.current"
                        :length="pagination.total"
                        circle
                        total-visible="8"
                        @input="getData"></v-pagination>

        </v-card-actions>

      </div>
    </v-card>

    <AddParcelDialog ref="addParcelDialogRef" @refresh="getData"/>
    <AddCardsDialog ref="addCardsDialogRef" @refresh="getData"/>

  </div>
</template>

<script>
import {HTTP} from "@/http-common";
import NoResults from "@/components/NoResults.vue";
import AddParcelDialog from "./AddParcelDialog";
import AddCardsDialog from "./AddCardsDialog.vue";

export default {
  components: {NoResults, AddParcelDialog, AddCardsDialog},
  data() {
    return {
      keyword: null,
      loadingStatus: {},
      isLoading: false,
      id: null,
      overlay: false,
      parcels: [],
      total: 0,
      perPage: 10,
      pagination: {
        current: 1,
        total: 0
      },
    };
  },
  methods: {
    statusColor(status) {
      switch (status) {
        case "created":
          return "blue lighten-2";
        case "shipped":
          return "orange lighten-2";
        case "delivered":
          return "green lighten-2";
        case "cancelled":
          return "red lighten-2";
        default:
          return "grey lighten-2";
      }
    },
    getData() {
      this.isLoading = true
      HTTP.get('/v1/refill-card-delivery/parcels?page=' + this.pagination.current, {
        params: {
          perPage: this.perPage,
          keyword: this.keyword
        }
      }).then(res => {

        this.pagination.current = res.data.data.current_page;
        this.pagination.total = res.data.data.last_page;
        this.total = res.data.data.total;

        this.parcels = res.data.data.data

        this.isLoading = false
      }).catch(err => {
        this.isLoading = false
        console.log(err)
      })
    },
    remove(id) {
      this.id = id;
      this.$confirm_dialog = true;
    },
    deleteItem() {
      this.overlay = true;
      HTTP.post("/v1/refill-card-delivery/parcels/delete", {
        id: this.id,
      }).then(() => {
        this.overlay = false;
        let i = this.parcels.findIndex((el) => el.id === this.id);
        this.$delete(this.parcels, i);
        this.id = null;
        this.$successMessage = "Cet élément a été supprimé avec succès.";
      }).catch((err) => {
        this.overlay = false;
        console.log(err);
      });
    },
    printOrderSlip(id) {
      this.overlay = true;
      HTTP.post(
          `/v1/refill-card-delivery/parcels/${id}/print-order-slip`,
          {},
          {
            responseType: 'blob',
          }
      ).then((res) => {
        this.overlay = false;

        if (!res.data || res.data.size === 0) {
          console.error('PDF is empty');
          return;
        }

        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bordereau_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
          .catch(err => {
            this.overlay = false;
            console.error(err);
          });
    }
  },
  computed: {
    pageStart() {
      if (this.total === 0) return 0
      return (this.pagination.current - 1) * this.perPage + 1
    },
    pageEnd() {
      let end = this.pagination.current * this.perPage
      return end > this.total ? this.total : end
    }
  },
  watch: {
    $is_confirm: function (el) {
      if (el) {
        this.deleteItem();
        this.$is_confirm = false;
      }
    },
    perPage() {
      this.pagination.current = 1
      this.getData();
    },
  },
  created() {
    this.getData();
  },
};
</script>

<style scoped>

</style>